import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../../../components/Navbar";
import API from "../../../services/api";

interface Product {
  _id: string;
  name: string;
  quantity?: number;
  image?: string;
  images?: string[];
}

const PRODUCTS_PER_PAGE = 20;

export default function ProductImages() {
  const [products, setProducts] = useState<Product[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [galleryImages, setGalleryImages] = useState<File[]>([]);
  const [success, setSuccess] = useState("");
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await API.get("/products", {
        params: {
          page,
          limit: PRODUCTS_PER_PAGE,
        },
      });

      const responseData = response.data?.data;
      const productList: Product[] = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.products)
        ? responseData.products
        : Array.isArray(response.data?.products)
        ? response.data.products
        : [];

      setProducts(productList);

      const pagination =
        responseData?.pagination || response.data?.pagination || {};

      setHasNextPage(
        typeof pagination.totalPages === "number"
          ? page < pagination.totalPages
          : typeof pagination.pages === "number"
          ? page < pagination.pages
          : productList.length === PRODUCTS_PER_PAGE
      );
    } catch (requestError: any) {
      setProducts([]);
      setError(
        requestError?.response?.data?.message || "Failed to load products."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const visibleProducts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return products;
    }

    return products.filter((product) =>
      product.name.toLowerCase().includes(normalizedSearch)
    );
  }, [products, search]);

  const selectProduct = (product: Product) => {
    setSelectedProduct(product);
    setCoverImage(null);
    setGalleryImages([]);
    setSuccess("");
    setError("");
  };

  const handleGalleryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    setGalleryImages((currentFiles) => {
      const filesByIdentity = new Map(
        currentFiles.map((file) => [
          `${file.name}-${file.size}-${file.lastModified}`,
          file,
        ])
      );

      selectedFiles.forEach((file) => {
        filesByIdentity.set(
          `${file.name}-${file.size}-${file.lastModified}`,
          file
        );
      });

      return Array.from(filesByIdentity.values());
    });

    // Allow choosing more files in a second picker action.
    event.target.value = "";
  };

  const removeGalleryImage = (fileToRemove: File) => {
    setGalleryImages((currentFiles) =>
      currentFiles.filter((file) => file !== fileToRemove)
    );
  };

  const handleUpdate = async (event: FormEvent) => {
    event.preventDefault();

    if (!selectedProduct) {
      setError("Please select a product.");
      return;
    }

    if (!coverImage && galleryImages.length === 0) {
      setError("Select a cover image or at least one gallery image.");
      return;
    }

    if (
      !window.confirm(
        `Update images for "${selectedProduct.name}"?`
      )
    ) {
      return;
    }

    try {
      setUpdating(true);
      setError("");
      setSuccess("");

      const formData = new FormData();

      if (coverImage) {
        formData.append("image", coverImage);
      }

      galleryImages.forEach((file) => {
        formData.append("images", file);
      });

      const response = await API.put(
        `/product/${selectedProduct._id}`,
        formData
      );

      setSuccess(
        response.data?.message || "Product images updated successfully."
      );
      setCoverImage(null);
      setGalleryImages([]);
      if (galleryInputRef.current) {
        galleryInputRef.current.value = "";
      }
      await fetchProducts();
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.response?.data?.error ||
          "Failed to update product images."
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Navbar />

      <main className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-7">
            <h1 className="text-3xl font-bold text-gray-900">
              Product Image Update
            </h1>
            <p className="mt-1 text-gray-500">
              Choose a product and update its cover or gallery images.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.25fr_0.75fr]">
            <section className="rounded-2xl border bg-white p-4 shadow-sm md:p-6">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Product List
                </h2>

                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search product..."
                  className="rounded-xl border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>

              {error && !selectedProduct && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-red-700">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="py-16 text-center text-gray-500">
                  Loading products...
                </div>
              ) : visibleProducts.length === 0 ? (
                <div className="py-16 text-center text-gray-500">
                  No products found.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibleProducts.map((product) => {
                    const isSelected = selectedProduct?._id === product._id;

                    return (
                      <button
                        key={product._id}
                        type="button"
                        onClick={() => selectProduct(product)}
                        className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${
                          isSelected
                            ? "border-violet-600 bg-violet-50 ring-1 ring-violet-600"
                            : "border-gray-200 hover:border-violet-300 hover:bg-gray-50"
                        }`}
                      >
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-2xl">
                              🌸
                            </div>
                          )}
                        </div>

                        <div className="min-w-0">
                          <div className="truncate font-semibold text-gray-900">
                            {product.name}
                          </div>
                          <div className="mt-1 text-sm text-gray-500">
                            Quantity: {product.quantity ?? 0}
                          </div>
                          <div className="mt-1 text-xs text-gray-400">
                            {product.images?.length || 0} gallery images
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-5 flex items-center justify-center gap-4">
                <button
                  type="button"
                  disabled={page === 1 || loading}
                  onClick={() => setPage((current) => current - 1)}
                  className="rounded-lg border px-4 py-2 disabled:opacity-40"
                >
                  ← Previous
                </button>
                <span className="text-sm text-gray-500">Page {page}</span>
                <button
                  type="button"
                  disabled={!hasNextPage || loading}
                  onClick={() => setPage((current) => current + 1)}
                  className="rounded-lg border px-4 py-2 disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            </section>

            <section className="h-fit rounded-2xl border bg-white p-5 shadow-sm md:p-6 lg:sticky lg:top-24">
              <h2 className="text-xl font-semibold text-gray-900">
                Update Images
              </h2>

              {selectedProduct ? (
                <div className="mt-2 rounded-xl bg-violet-50 p-3">
                  <div className="font-semibold text-violet-900">
                    {selectedProduct.name}
                  </div>
                  <div className="mt-1 break-all text-xs text-violet-700">
                    {selectedProduct._id}
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-500">
                  Select a product from the list.
                </p>
              )}

              {error && selectedProduct && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {success && (
                <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                  {success}
                </div>
              )}

              <form onSubmit={handleUpdate} className="mt-5 space-y-5">
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-gray-700">
                    Cover Image
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      setCoverImage(event.target.files?.[0] || null)
                    }
                    disabled={!selectedProduct || updating}
                    className="block w-full rounded-xl border border-gray-300 p-3 text-sm"
                  />
                  {coverImage && (
                    <span className="mt-2 block truncate text-xs text-gray-500">
                      {coverImage.name}
                    </span>
                  )}
                </label>

                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-gray-700">
                    Gallery Images
                  </span>
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryChange}
                    disabled={!selectedProduct || updating}
                    className="block w-full rounded-xl border border-gray-300 p-3 text-sm"
                  />
                  <span className="mt-2 block text-xs text-gray-500">
                    Select several images together, or choose more images again.
                    Each new selection will be added.
                  </span>

                  {galleryImages.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="text-sm font-semibold text-violet-700">
                        {galleryImages.length} images selected
                      </div>

                      {galleryImages.map((file) => (
                        <div
                          key={`${file.name}-${file.size}-${file.lastModified}`}
                          className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                        >
                          <span className="min-w-0 truncate text-xs text-gray-700">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(file)}
                            disabled={updating}
                            className="shrink-0 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </label>

                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-800">
                  The cover file is sent as <strong>image</strong>. All gallery
                  files are sent as repeated <strong>images</strong> fields.
                </div>

                <button
                  type="submit"
                  disabled={!selectedProduct || updating}
                  className="w-full rounded-xl bg-violet-600 px-5 py-3 font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {updating ? "Updating Images..." : "Update Product Images"}
                </button>
              </form>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}
